import React from 'react';
import { ChevronLeft, X } from 'lucide-react';
import { theme } from '../../theme';

/**
 * The app's one way to go BACK and one way to CLOSE.
 *
 * Both are real, outlined buttons with an icon and (for Back always, for
 * Close by default) a plain word, so people who don't read small text links
 * or bare icons still find their way out. Back goes up one step and says
 * where to ("Back to Dashboard"); Close leaves the screen or dialog entirely.
 * Touch targets are 44px on a phone.
 */

interface NavButtonBaseProps {
  onClick: () => void;
  /** 44px tall on a phone (the touch minimum); 36px otherwise. */
  touch?: boolean;
  disabled?: boolean;
  testId?: string;
  style?: React.CSSProperties;
}

const baseStyle = (touch: boolean, disabled: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  minHeight: touch ? 44 : 36,
  padding: '0 14px 0 10px',
  background: theme.colors.background.tertiary,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: 8,
  color: theme.colors.text.primary,
  font: 'inherit',
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1,
  flexShrink: 0,
  transition: `border-color ${theme.animations.duration.fast}ms ${theme.animations.easing.easeOut}`,
});

const hover = {
  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!e.currentTarget.disabled) e.currentTarget.style.borderColor = theme.colors.text.secondary;
  },
  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.style.borderColor = theme.colors.border.default;
  },
};

export interface BackButtonProps extends NavButtonBaseProps {
  /** Where it goes, in plain words: "Back to Dashboard", "All accounts". */
  label: string;
}

export const BackButton: React.FC<BackButtonProps> = ({ label, onClick, touch = false, disabled = false, testId, style }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    data-testid={testId}
    style={{ ...baseStyle(touch, disabled), ...style }}
    {...hover}
  >
    <ChevronLeft size={18} aria-hidden="true" style={{ color: theme.colors.text.accent, flexShrink: 0 }} />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
  </button>
);

export interface CloseButtonProps extends NavButtonBaseProps {
  /** The visible word; also the accessible name unless `ariaLabel` is given. */
  label?: string;
  /** Show only the ✕ (a square button) — the accessible name stays. */
  iconOnly?: boolean;
  ariaLabel?: string;
}

export const CloseButton: React.FC<CloseButtonProps> = ({
  label = 'Close',
  iconOnly = false,
  ariaLabel,
  onClick,
  touch = false,
  disabled = false,
  testId,
  style,
}) => {
  const size = touch ? 44 : 36;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel ?? (iconOnly ? label : undefined)}
      title={iconOnly ? (ariaLabel ?? label) : undefined}
      data-testid={testId}
      style={{
        ...baseStyle(touch, disabled),
        ...(iconOnly ? { padding: 0, width: size, minWidth: size } : {}),
        ...style,
      }}
      {...hover}
    >
      <X size={18} aria-hidden="true" style={{ flexShrink: 0 }} />
      {!iconOnly && <span>{label}</span>}
    </button>
  );
};
